import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "442",
  "slug": "satellite-vs-street-view-geography-games",
  "title": "Satellite vs. Street View: Two Ways to Play Geography Games",
  "excerpt": "The two most popular styles of online geography game start from completely different pictures. Here is how playing from satellite imagery compares to playing from street view, and which one suits the way you like to think.",
  "date": "2026-06-15",
  "category": "Community",
  "image": "https://images.unsplash.com/photo-1614730321146-b6fa6a46bcb4?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxzYXRlbGxpdGUlMjBlYXJ0aCUyMHZpZXd8ZW58MXwwfHx8MTc4MTQyNTA3Nnww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "Street view games drop you into a ground-level 360 photo, so you win by reading signs, road markings, and architecture. Satellite games show you the land from above, so you win by reading terrain, climate, and the patterns people leave on the landscape. They train different skills.",
  "content": [
    {
      "type": "paragraph",
      "text": "Type \"geography game\" into a search bar and you will mostly find two families of game. One drops you onto a road somewhere and lets you look around as if you were standing there. The other shows you a patch of the planet from directly above, the way a satellite sees it. Both ask the same question — where in the world am I? — but they hand you very different evidence to answer it."
    },
    {
      "type": "paragraph",
      "text": "Neither approach is better in some absolute sense. They reward different habits of attention, and a lot of players end up enjoying both. Here is how the two styles actually differ, and how to figure out which one fits you."
    },
    {
      "type": "heading",
      "text": "Two Different Pictures of the Same Planet"
    },
    {
      "type": "paragraph",
      "text": "Street view games are built on ground-level panoramic photography — the same kind of imagery you scroll through when you \"walk\" down a street in a mapping app. You are inside the scene, at human height, able to spin around and sometimes move along the road."
    },
    {
      "type": "paragraph",
      "text": "Satellite games are built on overhead imagery captured from aircraft or, increasingly, from Earth-observation satellites. You are looking straight down, reading the shape of the land and the marks people have made on it. There are no shop signs to read and no faces to see, but there is an enormous amount of structure once you know what to look for."
    },
    {
      "type": "heading",
      "text": "How Street View Games Work"
    },
    {
      "type": "paragraph",
      "text": "Standing on a virtual road, you become a detective of small details. The winning clues tend to be human and specific:"
    },
    {
      "type": "list",
      "items": [
        "The language and alphabet on road signs and shopfronts",
        "Which side of the road traffic drives on",
        "The style of bollards, road markings, and utility poles",
        "License plate shapes and colors when a car is in frame",
        "Architecture, vegetation, and the angle of the sun"
      ]
    },
    {
      "type": "paragraph",
      "text": "Because the clues are so concrete, street view play can feel like cracking a code. The flip side is that it only works where ground-level imagery exists — which is mostly along drivable roads in countries that have been photographed in detail."
    },
    {
      "type": "heading",
      "text": "How Satellite Games Work"
    },
    {
      "type": "paragraph",
      "text": "From above, you lose the signs and the alphabets, but you gain the big picture. The land itself becomes the clue:"
    },
    {
      "type": "list",
      "items": [
        "Climate and biome — desert, rainforest, tundra, or temperate farmland",
        "The shape and color of fields and how they are irrigated",
        "Road and settlement patterns, from gridded cities to scattered villages",
        "Coastlines, rivers, mountains, and how water moves through the land",
        "How densely buildings are packed, and how cities meet the countryside"
      ]
    },
    {
      "type": "paragraph",
      "text": "This rewards a more synthetic kind of thinking. You are rarely certain from a single detail; instead you stack several weak signals — dry climate, plus rectangular center-pivot fields, plus a particular road grid — into a confident guess."
    },
    {
      "type": "heading",
      "text": "Which Skills Each One Builds"
    },
    {
      "type": "paragraph",
      "text": "Street view sharpens your eye for cultural and linguistic detail. You start noticing the texture of a place — its signage, its cars, the way its streets are built. Satellite play builds physical and regional geography instead: where the world's deserts sit, how monsoon farmland differs from prairie, why a river bends the way it does. One makes you a better traveler; the other makes you better at reading the planet as a system."
    },
    {
      "type": "heading",
      "text": "Coverage and Fairness"
    },
    {
      "type": "paragraph",
      "text": "Coverage is where the two diverge most. Street view imagery thins out fast once you leave well-mapped road networks, so games built on it lean heavily toward a handful of densely photographed countries. Overhead imagery covers essentially the entire land surface — including deserts, ice sheets, islands, and places no camera car will ever reach — which makes a satellite game feel more evenly global and harder to game by memorizing a few common drop locations."
    },
    {
      "type": "paragraph",
      "text": "That coverage gap also changes how the two games age. Street view depends on imagery that has to be re-driven to stay current, so quiet rural roads can sit untouched for years. Satellites re-photograph the whole planet on a regular cadence, which means a satellite game can pull from imagery that is both global and reasonably fresh — and can show you parts of the world a camera car will simply never visit."
    },
    {
      "type": "heading",
      "text": "Which Should You Play?"
    },
    {
      "type": "paragraph",
      "text": "If you love spotting tiny tells and you enjoy the feeling of being somewhere, start with street view. If you would rather read landscapes, think about climate and terrain, and roam anywhere on Earth, start with satellite. Most people who get hooked on one eventually try the other, because the two styles end up reinforcing each other — the regional knowledge you build from above makes you faster at placing a street scene, and vice versa."
    },
    {
      "type": "paragraph",
      "text": "EarthGuessr sits firmly in the satellite camp: every round drops you onto a real place seen from above, and your job is to read the land and pin it on the map. If you have only ever played street view, it is a genuinely different puzzle — give it a round and see which way your brain prefers to work."
    }
  ]
};
